import { useDictionary } from "hooks/use-dictionary";
import FiltersAlertBase from "components/filters-alert";
import { useMemo } from "react";
import { PublicProgramFilters } from "types/program";
import { usePublicClassifierValues } from "hooks/api/system-admin/classifiers/use-public-classifier-values";
import { ClassifierType } from "types/classifier";
import { usePublicOrganizerInstitutionsForPrograms } from "hooks/api/use-public-organizer-institutions-for-programs";

interface FiltersAlertProps {
  filters: PublicProgramFilters;
}

interface InstitutionMap {
  [key: string]: string;
}

const FiltersAlert = ({ filters }: FiltersAlertProps) => {
  const dict = useDictionary();
  const { items: institutions } = usePublicOrganizerInstitutionsForPrograms(Boolean(filters.includeExpired));
  const { items: otherTargetedAudienceClassifiers } = usePublicClassifierValues(ClassifierType.OTHER_TARGETED_AUDIENCE);
  const { items: targetedAudienceClassifiers } = usePublicClassifierValues(ClassifierType.TARGETED_AUDIENCE);

  const institutionMap = useMemo(() => {
    return institutions.reduce((acc: InstitutionMap, institution) => {
      acc[institution.id] = institution.name;
      return acc;
    }, {} as InstitutionMap);
  }, [institutions]);

  const otherTargetedAudienceMap = useMemo(() => {
    return otherTargetedAudienceClassifiers.reduce((acc: Record<string, string>, classifier) => {
      acc[classifier.id] = classifier.valueName;
      return acc;
    }, {} as Record<string, string>);
  }, [otherTargetedAudienceClassifiers]);

  const targetedAudienceMap = useMemo(() => {
    return targetedAudienceClassifiers.reduce((acc: Record<string, string>, classifier) => {
      acc[classifier.id] = classifier.valueName;
      return acc;
    }, {} as Record<string, string>);
  }, [targetedAudienceClassifiers]);

  return (
    <FiltersAlertBase
      formatters={{
        name: (val) => `${dict.programs.programName} (${val})`,
        organizerId: (val) => {
          if (val !== null) {
            return `${dict.programs.programOrganizer} (${institutionMap[val] || val})`;
          }
          return "";
        },
        identification: (val) => `${dict.programs.identificationNumber} (${val})`,
        rating: (val) => `${dict.organizerAdmin.events.minimalReview} (${val})`,
        otherTargetedAudienceGroup: (val) => {
          if (val !== null) {
            return `${dict.organizerAdmin.events.otherTargetedAudienceClassifier} (${otherTargetedAudienceMap[val] || val})`;
          }
          return "";
        },
        targetedAudienceClassifierId: (val) => {
          if (val !== null) {
            return `${dict.organizerAdmin.events.targetedAudienceClassifier} (${targetedAudienceMap[val] || val})`;
          }
          return "";
        },
        includeExpired: () => dict.common.includeExpiredPrograms,
      }}
      filters={filters}
    />
  );
};

export default FiltersAlert;
